import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Card,
  CardContent,
  InputAdornment,
  Menu,
  ListItemIcon,
  Autocomplete,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  Avatar,
  Divider,
  CircularProgress,
} from '@mui/material';
import { 
  Download, 
  Print, 
  Refresh, 
  Search as SearchIcon,
  BookOnline as BookOnlineIcon,
  AttachMoney as AttachMoneyIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Clear as ClearIcon,
  Visibility,
  Close,
  Person,
  Home,
  CalendarToday,
  AccountBalance,
} from '@mui/icons-material';
import ExportButton from '../../components/ExportButton/ExportButton';
import RefreshButton from '../../components/RefreshButton/RefreshButton';
import { exportAsPDF, exportAsCSV, exportAsExcel } from '../../utils/importExport';
import { getAssociates } from '../../services/associate';
import { getBookingReports } from '../../services/reports';

const BookingReport = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    status: '',
    associateId: '',
    searchTerm: '',
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedAssociate, setSelectedAssociate] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [associates, setAssociates] = useState([]);
  const [associatesLoading, setAssociatesLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [totalBookings, setTotalBookings] = useState(0);
  const [bookingsLoading, setBookingsLoading] = useState(false);

  // Associate States aligned with CancelReport
  const [associateSearchInput, setAssociateSearchInput] = useState('');
  const associateSearchTimeoutRef = useRef(null);

  const mockAssociates = [
    { id: 'AS001', name: 'Rajesh Kumar', email: 'rajesh@company.com' },
    { id: 'AS002', name: 'Priya Sharma', email: 'priya@company.com' },
    { id: 'AS003', name: 'Vikram Singh', email: 'vikram@company.com' },
    { id: 'AS004', name: 'Amit Sharma', email: 'amit@company.com' },
    { id: 'AS005', name: 'Sneha Patel', email: 'sneha@company.com' },
    { id: 'AS006', name: 'Rahul Verma', email: 'rahul@company.com' },
    { id: 'AS007', name: 'Neha Kumar', email: 'neha@company.com' },
  ];

  const sampleData = [
    {
      sno: 1,
      bookingId: 'BK001',
      bookingDate: '2025-01-01',
      customerName: 'Amit Gupta',
      plotNo: 'P101',
      plotAmount: 500000,
      paidAmount: 100000,
      dueAmount: 400000,
      status: 'Booked',
      associate: 'Rajesh Kumar',
    },
    {
      sno: 2,
      bookingId: 'BK002',
      bookingDate: '2025-01-02',
      customerName: 'Jane Smith',
      plotNo: 'P102',
      plotAmount: 750000,
      paidAmount: 750000,
      dueAmount: 0,
      status: 'Alloted',
      associate: 'Priya Sharma',
    },
    {
      sno: 3,
      bookingId: 'BK003',
      bookingDate: '2025-01-03',
      customerName: 'Mike Johnson',
      plotNo: 'P103',
      plotAmount: 600000,
      paidAmount: 150000,
      dueAmount: 450000,
      status: 'Booked',
      associate: 'Vikram Singh',
    },
  ];

  const fetchAssociatesData = async (search = '') => {
    setAssociatesLoading(true);
    try {
      const response = await getAssociates({
        page: 1,
        limit: 10,
        searchTerm: search,
        search: search,
        searchKey: search,
        filterKey: search
      });

      let associatesData = [];
      if (response && response.data) {
        associatesData = Array.isArray(response.data) ? response.data : (response.data.data || response.data.docs || []);
      } else if (Array.isArray(response)) {
        associatesData = response;
      }

      const formatted = associatesData.map(associate => ({
        id: associate._id || associate.id || associate.associateId || associate.code,
        name: associate.name || associate.fullName || associate.firstName + ' ' + (associate.lastName || ''),
        email: associate.email || associate.emailAddress || '',
        phone: associate.phone || associate.mobile || associate.whatsapp || '',
        associateId: associate.associateId || associate.id || associate._id,
      }));

      setAssociates(formatted);
    } catch (error) {
      console.error("Error fetching associates:", error);
      setAssociates([]);
    } finally {
      setAssociatesLoading(false);
    }
  };

  useEffect(() => {
    fetchAssociatesData();
  }, []);

  useEffect(() => {
    if (!hasSearched && !searchLoading && !associatesLoading) {
      handleSearch();
    }
  }, [hasSearched, searchLoading, associatesLoading]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    if (hasSearched) {
      handleSearch(newPage, rowsPerPage);
    }
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setRowsPerPage(newLimit);
    setPage(0);
    if (hasSearched) {
      handleSearch(0, newLimit);
    }
  };

  const handleClearSearch = () => {
    setFilters({ ...filters, searchTerm: '' });
    setHasSearched(false);
    setPage(0);
  };

  const handleSearch = useCallback(async (p = page, limit = rowsPerPage) => {
    setSearchLoading(true);
    setBookingsLoading(true);
    setHasSearched(true);

    try {
      const apiParams = {
        page: p + 1,
        limit: limit,
      };

      if (filters.fromDate) {
        apiParams.fromDate = filters.fromDate;
      }
      if (filters.toDate) {
        apiParams.toDate = filters.toDate;
      }
      if (filters.status) {
        apiParams.status = filters.status.toLowerCase();
      }
      if (filters.associateId) {
        apiParams.associateId = filters.associateId;
      }
      if (filters.searchTerm) {
        apiParams.searchKey = filters.searchTerm;
      }

      const response = await getBookingReports(apiParams);

      let bookingsData = [];
      let totalCount = 0;

      if (response && response.data && Array.isArray(response.data.docs)) {
        bookingsData = response.data.docs;
        totalCount = response.data.totalDocs || 0;
      } else if (response && Array.isArray(response.data)) {
        bookingsData = response.data;
        totalCount = response.total || response.totalCount || response.data.length;
      } else if (Array.isArray(response)) {
        bookingsData = response;
        totalCount = response.length;
      }

      const transformedBookings = bookingsData.map((booking, index) => {
        const rawDate = booking.bookingDate || '';
        const formattedDate = rawDate ? new Date(rawDate).toLocaleDateString() : '';

        return {
          sno: (page * rowsPerPage) + index + 1,
          bookingId: booking.bookingId || booking.id || `BK${String((page * rowsPerPage) + index + 1).padStart(3, '0')}`,
          bookingDate: formattedDate,
          customerName: booking.customer?.name || '',
          plotNo: booking.particular?.inventoryDetail || '',
          plotAmount: booking.propertyAmount || booking.totalAmount || 0,
          discountAmount: booking.discount || booking.discountAmount || 0,
          authorityCharge: booking.authorityCharge || 0,
          paidAmount: booking.totalReceived || 0,
          dueAmount: booking.totalDueAmount || 0,
          status: booking.approvedStatus || 'pending',
          associate: booking.associate?.name || '',
          originalData: booking,
        };
      });

      setBookings(transformedBookings);
      setTotalBookings(totalCount);

    } catch (error) {
      setBookings([]);
      setTotalBookings(0);
    } finally {
      setSearchLoading(false);
      setBookingsLoading(false);
    }
  }, [page, rowsPerPage, filters, selectedAssociate]);

  const buildExportRows = () => {
    return bookings.map(booking => ({
      'S.No': booking.sno,
      'Booking ID': booking.bookingId,
      'Booking Date': booking.bookingDate,
      'Customer Name': booking.customerName,
      'Plot No': booking.plotNo,
      'Plot Amount': booking.plotAmount || 0,
      'Paid Amount': booking.paidAmount || 0,
      'Due Amount': booking.dueAmount || 0,
      'Status': booking.status,
      'Associate': booking.associate,
    }));
  };

  const handleExportPdf = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    
    const formattedRows = rows.map(r => ({
      ...r,
      'Plot Amount': `Rs. ${r['Plot Amount'].toLocaleString()}`,
      'Paid Amount': `Rs. ${r['Paid Amount'].toLocaleString()}`,
      'Due Amount': `Rs. ${r['Due Amount'].toLocaleString()}`,
    }));

    exportAsPDF({ rows: formattedRows, title: 'Booking Report', fileNameBase: 'booking_report', orientation: 'l' });
  };

  const handleExportCsv = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsCSV({ rows, fileNameBase: 'booking_report' });
  };

  const handleExportExcel = () => {
    const rows = buildExportRows();
    if (!rows.length) return;
    exportAsExcel({ rows, sheetName: 'Booking Report', fileNameBase: 'booking_report' });
  };

  const handleView = (booking) => {
    setSelectedBooking(booking);
    setViewDialogOpen(true);
  };

  const handleCloseViewDialog = () => {
    setViewDialogOpen(false);
    setSelectedBooking(null);
  };

  const getFilteredData = useCallback(() => {
    return bookings;
  }, [bookings]);

  const filteredData = useMemo(() => getFilteredData(), [getFilteredData]);

  const displayTotalBookings = hasSearched ? totalBookings : 0;

  return (
    <Box sx={{ p: 3, backgroundColor: '#f5f5f5', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, color: '#000', mb: 0.5 }}>
              Booking Report
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Track and manage plot bookings
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.2, alignItems: 'center' }}>
            <ExportButton
              onExportPdf={handleExportPdf}
              onExportCsv={handleExportCsv}
              onExportExcel={handleExportExcel}
            />
            <RefreshButton
              onClick={() => handleSearch(page, rowsPerPage)}
              loading={searchLoading || bookingsLoading}
              sx={{
                fontWeight: 600,
                px: 2,
                fontSize: '15px',
                borderRadius: 2,
                height: { xs: 'auto', sm: 40 }
              }}
            />
          </Box>
        </Box>
      </Box>


      {/* Filters */}
      <Card sx={{ mb: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.08)', borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#333', mb: 3 }}>
            Booking Filters
          </Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <TextField 
                fullWidth 
                size="small" 
                type="date" 
                label="From Date" 
                InputLabelProps={{ shrink: true }} 
                value={filters.fromDate}
                onChange={(e) => {
                  setFilters({ ...filters, fromDate: e.target.value });
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField 
                fullWidth 
                size="small" 
                type="date" 
                label="To Date" 
                InputLabelProps={{ shrink: true }} 
                value={filters.toDate}
                onChange={(e) => {
                  setFilters({ ...filters, toDate: e.target.value });
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select 
                  label="Status" 
                  value={filters.status}
                  onChange={(e) => {
                    setFilters({ ...filters, status: e.target.value });
                  }}
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="Booked">Booked</MenuItem>
                  <MenuItem value="Alloted">Alloted</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Autocomplete
                size="small"
                options={associates}
                filterOptions={(x) => x}
                loading={associatesLoading}
                getOptionLabel={(opt) => {
                  if (!opt) return '';
                  if (typeof opt === 'string') return opt;
                  const name = opt.name || 'N/A';
                  const mobile = opt.phone || opt.mobile || opt.whatsapp || '';
                  return mobile ? `${name} — ${mobile}` : `${name}`;
                }}
                value={selectedAssociate}
                inputValue={associateSearchInput}
                onInputChange={(event, newInputValue, reason) => {
                  if (event && event.type === 'blur') return;
                  setAssociateSearchInput(newInputValue);
                  if (reason === 'reset') return;

                  if (associateSearchTimeoutRef.current) {
                    clearTimeout(associateSearchTimeoutRef.current);
                  }
                  associateSearchTimeoutRef.current = setTimeout(() => {
                    fetchAssociatesData(newInputValue);
                  }, 500);
                }}
                onChange={(_, newValue) => {
                  setSelectedAssociate(newValue);
                  setFilters({ ...filters, associateId: newValue ? newValue.id : '' });
                  setAssociateSearchInput(newValue ? (newValue.phone ? `${newValue.name} — ${newValue.phone}` : newValue.name) : "");
                }}
                isOptionEqualToValue={(option, value) => {
                  if (!option || !value) return false;
                  return (option.id || option._id) === (value.id || value._id);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Associate"
                    placeholder="Search associate..."
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <React.Fragment>
                          {associatesLoading ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </React.Fragment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      }
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {option.name || 'N/A'}
                      </Typography>
                      {(option.phone || option.email) && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'flex', gap: 0.5 }}>
                          {option.phone || option.email}
                          {option.associateId && ` • ID: ${option.associateId}`}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                noOptionsText={associatesLoading ? "Loading associates..." : "No associates found"}
              />
            </Grid>
            {/* <Grid item xs={12} md={3}>
              <TextField 
                fullWidth 
                size="small" 
                label="Search Key" 
                placeholder="Search by ID, Name..." 
                value={filters.searchTerm}
                onChange={(e) => {
                  setFilters({ ...filters, searchTerm: e.target.value });
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
            </Grid> */}
          </Grid>

          {/* Search Controls */}
          <Box sx={{ mt: 3, display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
            <Tooltip title="Apply all filters and search" arrow>
              <Button
                variant="contained"
                onClick={() => handleSearch()}
                disabled={searchLoading}
                startIcon={searchLoading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  backgroundColor: '#536DFE',
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  '&:hover': {
                    backgroundColor: '#3F51B5',
                  },
                  '&:disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  }
                }}
              >
                {searchLoading ? 'Searching...' : 'Search'}
              </Button>
            </Tooltip>
            <Button
              variant="outlined"
              onClick={() => {
                setFilters({
                  fromDate: '',
                  toDate: '',
                  status: '',
                  associateId: '',
                  searchTerm: '',
                });
                setHasSearched(false);
                setSearchLoading(false);
                setSelectedAssociate(null);
                setPage(0);
                handleSearch(0, rowsPerPage);
              }}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderColor: '#536DFE',
                color: '#536DFE',
                borderRadius: 2,
                px: 3,
                py: 1,
                '&:hover': {
                  borderColor: '#3F51B5',
                  backgroundColor: '#f0f7ff',
                }
              }}
            >
              Reset
            </Button>
          </Box>
        </CardContent>
      </Card>
     


      {/* Search Results Summary */}


      {/* Table */}
      <Card sx={{ boxShadow: '0 2px 12px rgba(0,0,0,0.1)', borderRadius: 2, mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Booking Details
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              {hasSearched ? `${displayTotalBookings} booking${displayTotalBookings !== 1 ? 's' : ''} found` : 'Use filters above and click Search to view bookings'}
            </Typography>
          </Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#536DFE' }}>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>S.No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking ID</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Booking Date</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Plot No</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Plot Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Paid Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Due Amount</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Status</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px' }}>Associate</TableCell>
                  <TableCell sx={{ color: '#fff', fontWeight: 700, fontSize: '14px', textAlign: 'center' }}>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(searchLoading || bookingsLoading) ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={24} />
                        <Typography variant="body2" color="text.secondary">
                          {searchLoading ? 'Searching bookings...' : 'Loading bookings...'}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : !hasSearched ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <BookOnlineIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" sx={{ mb: 1 }}>
                          Ready to View Bookings
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Apply filters above and click Search to view booking reports
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : filteredData.length > 0 ? (
                  filteredData.map((row) => (
                  <TableRow 
                    key={row.sno} 
                    hover
                    sx={{
                      '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' },
                      '&:hover': { backgroundColor: '#f0f7ff !important' }
                    }}
                  >
                    <TableCell>{row.sno}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.bookingId}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.bookingDate}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{row.plotNo}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Rs. {row.plotAmount.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#2e7d32' }}>Rs. {row.paidAmount.toLocaleString()}</TableCell>
                    <TableCell sx={{ fontWeight: 600, color: row.dueAmount > 0 ? '#d32f2f' : '#2e7d32' }}>
                      Rs. {row.dueAmount.toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={row.status}
                        size="small"
                        color={row.status === 'approved' ? 'success' : row.status === 'booked' ? 'primary' : 'warning'}
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell>{row.associate}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleView(row)}
                          sx={{ color: '#536DFE' }}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography variant="body1" color="text.secondary">
                        No bookings found matching your search criteria
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          {/* Pagination */}
          {hasSearched && displayTotalBookings > 0 && (
            <TablePagination
              
              component="div"
              count={displayTotalBookings}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          )}
        </CardContent>
      </Card>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={handleCloseViewDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 2 }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          backgroundColor: '#536DFE',
          color: 'white',
          px: 3,
          py: 2
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Visibility />
            <Typography variant="h6" fontWeight="bold">
              Booking Details
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseViewDialog}
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3 }}>
          {selectedBooking && (
            <>
              {/* Header Section */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 2, 
                mb: 3,
                p: 2,
                backgroundColor: '#f8f9fa',
                borderRadius: 2,
                border: '1px solid #e9ecef'
              }}>
                <Avatar sx={{ bgcolor: '#536DFE', width: 48, height: 48 }}>
                  <Person />
                </Avatar>
                <Box>
                  <Typography variant="h6" fontWeight="bold">
                    {selectedBooking.customerName}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Booking ID: {selectedBooking.bookingId}
                  </Typography>
                  <Chip 
                    label={selectedBooking.status} 
                    size="small" 
                    color={selectedBooking.status === 'Alloted' ? 'success' : 'primary'}
                    sx={{ mt: 0.5 }}
                  />
                </Box>
              </Box>

              <Grid container spacing={3}>
                {/* Property Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Home color="primary" />
                        Property Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Plot Number:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBooking.plotNo}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Booking Date:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBooking.bookingDate}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Associate:</Typography>
                          <Typography variant="body2" fontWeight="600">{selectedBooking.associate}</Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Financial Details */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ height: '100%' }}>
                    <CardContent>
                      <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AccountBalance color="primary" />
                        Financial Details
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Plot Amount:</Typography>
                          <Typography variant="body2" fontWeight="600">₹{selectedBooking.plotAmount.toLocaleString()}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Discount:</Typography>
                          <Typography variant="body2" fontWeight="600" color="info.main">₹{(selectedBooking.discountAmount || 0).toLocaleString()}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Auth. Charge:</Typography>
                          <Typography variant="body2" fontWeight="600" color="warning.main">₹{(selectedBooking.authorityCharge || 0).toLocaleString()}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Paid Amount:</Typography>
                          <Typography variant="body2" fontWeight="600" color="success.main">₹{selectedBooking.paidAmount.toLocaleString()}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Due Amount:</Typography>
                          <Typography variant="body2" fontWeight="600" color={selectedBooking.dueAmount > 0 ? 'error.main' : 'success.main'}>
                            ₹{selectedBooking.dueAmount.toLocaleString()}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Additional Information */}
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday color="primary" />
                    Additional Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#f8f9fa', borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" color="primary">
                          {((selectedBooking.paidAmount / selectedBooking.plotAmount) * 100).toFixed(1)}%
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Payment Progress</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: '#e8f5e8', borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                          ₹{selectedBooking.paidAmount.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Total Paid</Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <Box sx={{ textAlign: 'center', p: 2, backgroundColor: selectedBooking.dueAmount > 0 ? '#ffeaea' : '#e8f5e8', borderRadius: 2 }}>
                        <Typography variant="h6" fontWeight="bold" color={selectedBooking.dueAmount > 0 ? 'error.main' : 'success.main'}>
                          ₹{selectedBooking.dueAmount.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">Remaining Amount</Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, backgroundColor: '#f8f9fa' }}>
          <Button 
            onClick={handleCloseViewDialog}
            variant="contained"
            sx={{ 
              backgroundColor: '#536DFE',
              '&:hover': { backgroundColor: '#3F51B5' }
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default BookingReport;

